import { ActionsUnion, createAction } from 'utils/redux'
import { getCurrentWindowId } from 'utils'
import { PreparedStagingOrder, WindowSelections } from './selectionTypes'
import { GridId, GridSettingsId } from '../grid'
import { EntityTypeEnum, OrderDetail, SubscriptionFilter, SubscriptionIdentifier } from 'api'
import { SHOULD_AUTOBATCH } from '@reduxjs/toolkit'

export enum SelectionActionTypes {
  /* Session */
  OPEN_ACTIVITY_MONITOR = 'OPEN_ACTIVITY_MONITOR',
  SET_COLUMNS_AUTO_SIZED = 'SET_COLUMNS_AUTO_SIZED',

  /* Window */
  INITIALISE_WINDOW = 'INITIALISE_WINDOW',
  PREPARE_STAGING_ORDERS = 'PREPARE_STAGING_ORDERS',
  UNPREPARE_STAGING_ORDERS = 'UNPREPARE_STAGING_ORDERS',
  SET_PREPARED_STAGING_ORDERS = 'SET_PREPARED_STAGING_ORDERS',
  SET_PREPARED_COLLECTION_ID = 'SET_PREPARED_COLLECTION_ID',
  UNPREPARE_STAGING_ALL = 'UNPREPARE_STAGING_ALL',
  SELECT_CURRENT_GRID_CONFIG = 'SELECT_CURRENT_GRID_CONFIG',
  TOGGLE_LEFT_NAVIGATION = 'TOGGLE_LEFT_NAVIGATION',
  TOGGLE_PINNED_PREPARE_RFQ_PANEL = 'PIN_PREPARE_RFQ_PANEL',

  // audit subscription
  ADD_AUDIT_SUBSCRIPTIONS = 'ADD_AUDIT_SUBSCRIPTIONS',
  REMOVE_AUDIT_SUBSCRIPTIONS = 'REMOVE_AUDIT_SUBSCRIPTIONS',
  CLEAR_AUDIT_SUBSCRIPTIONS = 'CLEAR_AUDIT_SUBSCRIPTIONS',

  // global view
  SET_ORGANISATION_ID = 'SET_ORGANISATION_ID',

  // organisation profiles
  OPEN_ORGANISATION_PROFILES = 'OPEN_ORGANISATION_PROFILES',
  CLOSE_ORGANISATION_PROFILES = 'CLOSE_ORGANISATION_PROFILES',

  // select collection
  SET_COLLECTION_ID = 'SET_COLLECTION_ID',
  CLEAR_COLLECTION_ID = 'CLEAR_COLLECTION_ID',
  CLEAR_ALL_COLLECTION_IDS = 'CLEAR_ALL_COLLECTION_IDS',

  // select counterparty
  SET_COUNTERPARTY_ID = 'SET_COUNTERPARTY_ID',
  CLEAR_COUNTERPARTY_ID = 'CLEAR_COUNTERPARTY_ID',

  // navigation collapse
  SET_NAVIGATION_COLLAPSED = 'SET_NAVIGATION_COLLAPSED',

  // toggle hide expired/rejected negotiations in initiated grid
  TOGGLE_HIDE_EXPIRED_REJECTED = 'TOGGLE_HIDE_EXPIRED_REJECTED',

  // set current grid id in view
  SET_CURRENT_GRID_ID = 'SET_CURRENT_GRID_ID',

  // set activity highlight for initiated/incoming/deals grid
  SET_GRID_ACTIVITY_HIGHLIGHT = 'SET_GRID_ACTIVITY_HIGHLIGHT',

  // set grid row in edit
  SET_GRID_ROW_IN_EDIT = 'SET_GRID_ROW_IN_EDIT',

  // set order id for related deals/open repo grid
  SET_DEAL_ID_FOR_RELATED_DEAL_TRADES = 'SET_DEAL_ID_FOR_RELATED_DEAL_TRADES',
  SET_DEAL_ID_FOR_RELATED_OPEN_REPO_TRADES = 'SET_DEAL_ID_FOR_RELATED_OPEN_REPO_TRADES',
  SET_DEAL_ID_FOR_RELATED_STRUCTURED_TRADES = 'SET_DEAL_ID_FOR_RELATED_STRUCTURED_TRADES',
  SET_DEAL_ID_FOR_RELATED_MID_TRADE_EVENT_INITIATED = 'SET_DEAL_ID_FOR_RELATED_MID_TRADE_EVENT_INITIATED',
  SET_DEAL_ID_FOR_RELATED_MID_TRADE_EVENT_INCOMING = 'SET_DEAL_ID_FOR_RELATED_MID_TRADE_EVENT_INCOMING',

  // manage subscriptions
  ADD_ENTITY_SUBSCRIPTION = 'ADD_ENTITY_SUBSCRIPTION',
  REMOVE_ENTITY_SUBSCRIPTION = 'REMOVE_SUBSCRIPTION',
  ADD_SUBSCRIPTION_PAGE_DETAILS = 'ADD_SUBSCRIPTION_PAGE_DETAILS',
  EDIT_SUBSCRIPTION_PAGE_DETAILS = 'EDIT_SUBSCRIPTION_PAGE_DETAILS'
}

export const SelectionActions = {
  openActivityMonitor: (activityMonitorOpen: boolean) => createAction(SelectionActionTypes.OPEN_ACTIVITY_MONITOR, { activityMonitorOpen }),
  setColumnsAutoSized: (gridId: GridId, autoSized: boolean) =>
    createAction(SelectionActionTypes.SET_COLUMNS_AUTO_SIZED, { gridId, autoSized }),
  initialiseWindow: (selections: Partial<WindowSelections>) =>
    createAction(SelectionActionTypes.INITIALISE_WINDOW, { selections, windowId: getCurrentWindowId() }),
  prepareStagingOrders: (orderDetails: OrderDetail[]) =>
    createAction(SelectionActionTypes.PREPARE_STAGING_ORDERS, { orderDetails, windowId: getCurrentWindowId() }),
  unprepareStagingOrders: (orderIds: string[]) =>
    createAction(SelectionActionTypes.UNPREPARE_STAGING_ORDERS, { orderIds, windowId: getCurrentWindowId() }),
  setPreparedStagingOrders: (preparedStagingOrders: PreparedStagingOrder[], windowId = getCurrentWindowId()) =>
    createAction(SelectionActionTypes.SET_PREPARED_STAGING_ORDERS, { preparedStagingOrders, windowId }),
  setPreparedCollectionId: (preparedCollectionId: string | null, windowId = getCurrentWindowId()) =>
    createAction(SelectionActionTypes.SET_PREPARED_COLLECTION_ID, { preparedCollectionId, windowId }),
  unprepareStagingAll: () => createAction(SelectionActionTypes.UNPREPARE_STAGING_ALL, { windowId: getCurrentWindowId() }),
  selectCurrentGridConfig: (gridId: GridId, gridSettingsId: GridSettingsId) =>
    createAction(SelectionActionTypes.SELECT_CURRENT_GRID_CONFIG, {
      gridId,
      gridSettingsId,
      windowId: getCurrentWindowId()
    }),
  toggleLeftNavigation: () => createAction(SelectionActionTypes.TOGGLE_LEFT_NAVIGATION, { windowId: getCurrentWindowId() }),
  pinPrepareRFQPanel: (isPinned: boolean) =>
    createAction(SelectionActionTypes.TOGGLE_PINNED_PREPARE_RFQ_PANEL, { isPinned, windowId: getCurrentWindowId() }),
  addAuditSubscriptions: (orderIds: string[]) =>
    createAction(SelectionActionTypes.ADD_AUDIT_SUBSCRIPTIONS, { orderIds, windowId: getCurrentWindowId() }),
  removeAuditSubscriptions: (orderIds: string[]) =>
    createAction(SelectionActionTypes.REMOVE_AUDIT_SUBSCRIPTIONS, { orderIds, windowId: getCurrentWindowId() }),
  clearAuditSubscriptions: () => createAction(SelectionActionTypes.CLEAR_AUDIT_SUBSCRIPTIONS, { windowId: getCurrentWindowId() }),
  setOrganisationId: (organisationId: string | null) =>
    createAction(SelectionActionTypes.SET_ORGANISATION_ID, { organisationId, windowId: getCurrentWindowId() }),
  openOrganisationProfiles: (organisationIds: string[]) =>
    createAction(SelectionActionTypes.OPEN_ORGANISATION_PROFILES, { organisationIds, windowId: getCurrentWindowId() }),
  closeOrganisationProfiles: (organisationIds: string[]) =>
    createAction(SelectionActionTypes.CLOSE_ORGANISATION_PROFILES, { organisationIds, windowId: getCurrentWindowId() }),
  setCollectionId: (gridId: GridId, collectionId: string | null) =>
    createAction(SelectionActionTypes.SET_COLLECTION_ID, { gridId, collectionId, windowId: getCurrentWindowId() }),
  clearCollectionId: (gridId: GridId) =>
    createAction(SelectionActionTypes.CLEAR_COLLECTION_ID, {
      gridId,
      windowId: getCurrentWindowId()
    }),
  setCounterpartyId: (gridId: GridId, counterpartyId: string | null) =>
    createAction(SelectionActionTypes.SET_COUNTERPARTY_ID, { gridId, counterpartyId, windowId: getCurrentWindowId() }),
  clearCounterpartyId: (gridId: GridId) =>
    createAction(SelectionActionTypes.CLEAR_COUNTERPARTY_ID, { gridId, windowId: getCurrentWindowId() }),
  clearAllCollectionIds: () => createAction(SelectionActionTypes.CLEAR_ALL_COLLECTION_IDS, { windowId: getCurrentWindowId() }),
  setNavigationCollapsed: (navigationEntry: string, collapsed: boolean) =>
    createAction(SelectionActionTypes.SET_NAVIGATION_COLLAPSED, {
      navigationEntry,
      collapsed,
      windowId: getCurrentWindowId()
    }),
  setCurrentGridId: (gridId: GridId | null) =>
    createAction(SelectionActionTypes.SET_CURRENT_GRID_ID, { gridId, windowId: getCurrentWindowId() }),
  setGridActivityHighlight: (gridId: GridId, showHighlight: boolean, debounce: boolean = false) =>
    createAction(
      SelectionActionTypes.SET_GRID_ACTIVITY_HIGHLIGHT,
      {
        gridId,
        showHighlight,
        windowId: getCurrentWindowId()
      },
      { [SHOULD_AUTOBATCH]: debounce }
    ),
  setGridRowInEdit: (rowId: string | null, rowData: any) =>
    createAction(SelectionActionTypes.SET_GRID_ROW_IN_EDIT, { rowIndex: rowId, rowData, windowId: getCurrentWindowId() }),
  setDealIdForRelatedDealTrades: (dealId: string | null) =>
    createAction(SelectionActionTypes.SET_DEAL_ID_FOR_RELATED_DEAL_TRADES, { dealId, windowId: getCurrentWindowId() }),
  setDealIdForRelatedOpenRepoTrades: (dealId: string | null) =>
    createAction(SelectionActionTypes.SET_DEAL_ID_FOR_RELATED_OPEN_REPO_TRADES, {
      dealId,
      windowId: getCurrentWindowId()
    }),
  setDealIdForRelatedStructuredTrades: (dealId: string | null) =>
    createAction(SelectionActionTypes.SET_DEAL_ID_FOR_RELATED_STRUCTURED_TRADES, {
      dealId,
      windowId: getCurrentWindowId()
    }),
  setDealIdForRelatedMidTradeEventInitiated: (dealId: string | null) =>
    createAction(SelectionActionTypes.SET_DEAL_ID_FOR_RELATED_MID_TRADE_EVENT_INITIATED, {
      dealId,
      windowId: getCurrentWindowId()
    }),
  setDealIdForRelatedMidTradeEventIncoming: (dealId: string | null) =>
    createAction(SelectionActionTypes.SET_DEAL_ID_FOR_RELATED_MID_TRADE_EVENT_INCOMING, {
      dealId,
      windowId: getCurrentWindowId()
    }),
  addEntitySubscription: (
    entityType: EntityTypeEnum,
    subscriptionIdentifier: SubscriptionIdentifier,
    subscriptionFilters: SubscriptionFilter[]
  ) =>
    createAction(SelectionActionTypes.ADD_ENTITY_SUBSCRIPTION, {
      entityType,
      subscriptionIdentifier,
      subscriptionFilters
    }),
  removeEntitySubscription: (entityType: EntityTypeEnum, subscriptionIdentifier: SubscriptionIdentifier) =>
    createAction(SelectionActionTypes.REMOVE_ENTITY_SUBSCRIPTION, { entityType, subscriptionIdentifier }),
  addSubscriptionPageDetails: (
    entityType: EntityTypeEnum,
    subscriptionIdentifier: SubscriptionIdentifier,
    page: number,
    totalPages: number,
    resultCount: number,
    totalResultCount: number
  ) =>
    createAction(SelectionActionTypes.ADD_SUBSCRIPTION_PAGE_DETAILS, {
      entityType,
      subscriptionIdentifier,
      page,
      totalPages,
      resultCount,
      totalResultCount
    }),
  editSubscriptionPageDetails: (
    entityType: EntityTypeEnum,
    subscriptionIdentifier: SubscriptionIdentifier,
    page: number,
    totalPages: number
  ) =>
    createAction(SelectionActionTypes.EDIT_SUBSCRIPTION_PAGE_DETAILS, {
      entityType,
      subscriptionIdentifier,
      page,
      totalPages
    })
}

export type SelectionActions = ActionsUnion<typeof SelectionActions>
